"use client";

import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCcw, Home, MessageCircle } from "lucide-react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const t = useTranslations("Error");

  useEffect(() => {
    // Log the error
    console.error(error);
  }, [error]);

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-slate-950 px-4 py-20">
      {/* Background with danger color cues */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-red-900/20 rounded-full blur-[120px]" />
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-red-500/10 rounded-full blur-[100px] animate-pulse" />
      
      <div className="relative z-10 max-w-2xl w-full text-center space-y-12">
        <div
            className="flex flex-col items-center"
        >
            <div className="w-24 h-24 rounded-3xl bg-red-950/50 border border-red-500/30 flex items-center justify-center mb-8 backdrop-blur-xl ring-8 ring-red-900/10 animate-shake">
                <AlertTriangle className="w-12 h-12 text-red-500" />
            </div>
            
            <h1 className="text-4xl md:text-6xl font-extrabold font-heading text-white tracking-tighter mb-4">
              {t("title")}
            </h1>
            <p className="text-xl text-slate-400 leading-relaxed max-w-lg mx-auto">
              {t("description")}
            </p>
        </div>

        <div 
            className="flex flex-col sm:flex-row gap-5 justify-center items-center"
        >
          <Button 
            size="lg" 
            onClick={() => reset()} 
            className="h-14 px-10 text-lg font-bold bg-red-600 hover:bg-red-700 shadow-2xl shadow-red-900/40 active:scale-95 transition-all group"
          >
            <RefreshCcw className="me-2 w-5 h-5 group-hover:rotate-180 transition-transform duration-700" /> {t("retry")}
          </Button>

          <Button 
            variant="outline" 
            size="lg" 
            asChild
            className="h-14 px-10 text-lg font-bold text-white border-white/10 bg-white/5 hover:bg-white/10 backdrop-blur-md active:scale-95 transition-all"
          >
            <Link href="/">
                <Home className="me-2 w-5 h-5" /> {t("back_home")}
            </Link>
          </Button>
        </div>

        {/* Support floating button */}
        <div
            className="pt-10 flex items-center justify-center gap-2 text-slate-500 text-sm font-medium tracking-wide uppercase italic"
        >
             <MessageCircle className="w-4 h-4" /> 
             {t("need_help")}
        </div>
      </div>
    </div>
  );
}
